import styles from "./auto-loan-affordability.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as graphUtil from "shared/graphs/util"
import Line from "story/components/graphs/line"
import {DoubleBar} from "story/components/graphs/bar"
import * as loan from "shared/loan-payoff"
import * as finance from "shared/finance"
import * as date from "shared/date"
import AccessibilityTable from "activities/components/shared/accessibility-table"

/* Data for graph format:
[
[year, value],
[year, value],
[year, value]
]
*/

const graphData = (balance, rate, payment, term) => {
  let interest = 0
  const data = Array.from(Array(term + 1)).reduce((memo, x, idx) => {
    if (idx === 0) memo.push([idx, balance, 0])
    else {
      const last = memo[memo.length - 1]
      const bal = last[1]
      const towardPrincipal = loan.getPrincipalPayment(bal, payment, rate)
      interest += payment - towardPrincipal
      memo.push([idx, loan.minusPayment(bal, towardPrincipal), interest])
    }
    return memo
  }, [])

  return {data, interest}
}

const calcThings = ({monthlyPayment, downPayment, term, rate}) => {
  const carCost = downPayment + loan.calculateLoanAmount(monthlyPayment, rate, term)
  const maxLoan = carCost - downPayment
  const {data, interest} = graphData(maxLoan, rate, monthlyPayment, term)

  return {
    carCost,
    maxLoan,
    interestPaid: interest,
    data
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {monthlyPayment, downPayment, term, rate, resetKey} = state
    const {id, theme} = getProps()
    const {carCost, maxLoan, interestPaid, data} = calcThings({monthlyPayment, downPayment, term, rate: rate / 100})

    const accessibilityTableHeadings = [
      {name: "Months", key: "month"},
      {name: "Loan Balance", key: "balance"},
      {name: "Interest", key: "interest"}
    ]

    const accessibilityTableData = data.map((row) => ({
      month: row[0],
      balance: util.formatDollarsNoCents(row[1]),
      interest: util.formatDollarsNoCents(row[2])
    }))

    const accessibilityTableLabel = "Auto Loan Affordability Table"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer>
              <inputs.CalcInput
                id={id}
                title={"Preferred Monthly Payment"}
                onChange={updateState("monthlyPayment", util.fromString)}
                initialValue={monthlyPayment}
                resetKey={resetKey}
              />
              <inputs.SmallCalcInputWithTitle id={id} title="Down Payment" onChange={updateState("downPayment", util.fromString)} initialValue={downPayment} resetKey={resetKey} />
              <inputs.RangeInput
                id={id}
                title="Preferred Loan Term"
                onChange={updateState("term", (v) => v * 12)}
                initialValue={term / 12}
                min={1}
                max={10}
                step={1}
                units="years"
                valueType="years-full"
                resetKey={resetKey}
              />
              <inputs.RangeInput
                id={id}
                title="Interest Rate"
                onChange={updateState("rate", parseFloat)}
                initialValue={rate}
                min={0}
                max={30}
                step={0.05}
                units="%"
                valueType="percent"
                resetKey={resetKey}
              />
            </shared.InputsContainer>
            <shared.ChartContainer>
              <DoubleBar
                isStacked={true}
                yAxis="Loan Amount"
                xAxis={"Months"}
                values={data}
                tooltip={[
                  "Months",
                  {name: "Loan Balance", type: "currency", color: graphUtil.getColor(theme, 1)},
                  {name: "Interest", type: "currency", color: graphUtil.getColor(theme, 4)}
                ]}
                type="calculator"
                ariaLabel="This line graph visually represents the payoff of your car loan, based on your input. Read below to find the car cost, the max loan you can afford, and the interest you would pay."
                colors={[graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 4)]}
                formatter="currency"
              />
            </shared.ChartContainer>
            <shared.SummaryContainer className={styles["summary"]}>
              <callouts.Callout>
                <callouts.Detail label="Car Cost" result={util.formatDollarsNoCents(util.ifNegative(carCost))} dataTestId="car-cost" />
                <callouts.Detail label="Max Loan" result={util.formatDollarsNoCents(util.ifNegative(maxLoan))} dataTestId="max-loan" />
                <callouts.Detail label="Interest Paid" result={util.formatDollarsNoCents(interestPaid)} dataTestId="interest-paid" />
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {carCost, maxLoan, interestPaid} = calcThings(state)
      return Object.assign(state, {carCost, maxLoan, interestPaid})
    },
    defaultProps: {
      id: "auto-loan-affordability",
      className: "auto-loan-affordability",
      title: "Auto Loan Affordability",
      subtitle: "Explore how much car you can afford based on payment preferences."
    },
    defaultState: {
      monthlyPayment: 400,
      downPayment: 1000,
      term: 36,
      rate: 3.75
    }
  }
)
